/**
 * 出庫單詳情 · **交收證據**（P1-9；`ui-spec` 頁 9；`rules §七 H2`／`H3`／`H4`／`H5`）
 *
 * 做兩件事
 *   ① ⭐ **H4 紅標**：`delivery` 單交收之後冇「客戶印章／簽名」相 → 顯示「⚠️ 缺交收證據」
 *      （判斷一律睇後端 `order.deliveryEvidence`，前端唔准自己砌狀態）
 *   ② 上載「簽咗嘅送貨單」相（**相可以後補** —— H2；物流／職員／管理員都可以補）
 *
 * 紅線
 *   · ⚠️ 證據 **append-only**（H3）：只能新增，**冇刪除／覆寫** —— 上載錯就再影一張
 *   · ⚠️ 睇相一律經 `/api/evidence/:id`（session 串流；R2 私密，H5）—— 唔准擺公開 URL
 *   · ⚠️ 上載前**一定**要填 `kind = delivery_note_chop`（唔係第日搵唔返邊張係交收證據）
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { btnPrimary, card, errText, hint as hintStyle } from '@coeldery/ui-kit';
import { ApiError, api, type EvidenceItem, type Me } from '../lib/api';

/** 交收證據嘅受控 `kind`（同後端／`@coeldery/pdf-report` 一致） */
const CHOP_KIND = 'delivery_note_chop';

export default function DeliveryEvidencePanel({
  orderId,
  hasChop,
  missing,
  user,
  onUploaded,
}: {
  orderId: string;
  /** 後端 DTO `hasDeliveryNoteChop` */
  hasChop: boolean;
  /** 後端 DTO `deliveryEvidence.missing`（H4） */
  missing: boolean;
  user: Me;
  onUploaded: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [list, setList] = useState<EvidenceItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.evidence('outbound_order', orderId);
      setList(res.evidence.filter((e) => e.kind === CHOP_KIND));
    } catch {
      /* 清單失敗唔阻塞主流程（紅標照顯示） */
    }
  }, [orderId]);

  useEffect(() => {
    void load();
  }, [load]);

  /** 補相：office 同事或物流（H2／`decisions/006` §1）——供應商冇份（後端 403） */
  const canUpload = user.role !== 'supplier';

  const upload = async (file: File | null) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await api.uploadEvidence(
        { entityType: 'outbound_order', entityId: orderId, kind: CHOP_KIND },
        file,
      );
      await load();
      await onUploaded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('out.uploadFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: 800 }}>{t('out.evidence')}</h3>
      {missing ? (
        <p style={{ ...hintStyle, color: 'var(--color-accent)', fontWeight: 800 }} role="alert">
          {t('out.missingEvidence')}
        </p>
      ) : hasChop ? (
        <p style={{ ...hintStyle, fontWeight: 700 }}>{t('out.evidenceOk')}</p>
      ) : (
        <p style={hintStyle}>{t('out.evidenceNotYet')}</p>
      )}

      {list.length > 0 && (
        <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {list.map((e) => (
            <li key={e.id}>
              <a href={e.url} target="_blank" rel="noreferrer">
                <img
                  src={e.url}
                  alt={t('out.chopPhoto')}
                  style={{ width: '120px', height: '90px', objectFit: 'cover', border: '1px solid var(--color-divider)', borderRadius: '6px' }}
                />
              </a>
              <p style={{ ...hintStyle, margin: '2px 0 0' }}>{e.uploadedAt.slice(0, 16).replace('T', ' ')}</p>
            </li>
          ))}
        </ul>
      )}

      {canUpload ? (
        <div style={{ marginTop: '10px' }}>
          <label style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', cursor: busy ? 'default' : 'pointer' }}>
            {busy ? t('common.saving') : t('out.uploadChop')}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              disabled={busy}
              onChange={(ev) => void upload(ev.target.files?.[0] ?? null)}
            />
          </label>
          <p style={{ ...hintStyle, marginTop: '6px' }}>{t('out.uploadChopHint')}</p>
        </div>
      ) : (
        <p style={{ ...hintStyle, marginTop: '8px' }}>{t('out.evidenceSupplierNoAccess')}</p>
      )}

      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
